import { supabase } from '@/lib/supabase';
import { apiService } from './apiService';

export interface Checkin {
  id: string;
  user_id: string;
  meeting_id: string;
  checked_in_at: string;
  meeting_date: string;
  device_info?: string;
  location?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface CheckinRequest {
  meetingId: string;
  meetingDate: string;
  deviceInfo?: string;
  location?: string;
  notes?: string;
  onNoteCreate?: (content: string, type?: 'note' | 'trail' | 'sentiment' | 'checkin' | 'relapse') => Promise<void>; // Optional callback for creating diary notes
}

export interface CheckinResponse {
  success: boolean;
  checkin?: Checkin;
  alreadyCheckedIn?: boolean;
  meeting?: {
    id: string;
    name: string;
    link: string;
  };
  message: string;
  error?: string;
}

export interface CheckinStatus {
  success: boolean;
  isCheckedIn: boolean;
  checkin?: Checkin;
  error?: string;
}

export interface CheckinHistory {
  success: boolean;
  checkins: (Checkin & {
    meetings?: {
      meeting_name: string;
      meeting_description?: string;
      meeting_link?: string;
      meeting_time: string;
      meeting_type?: string;
    };
  })[];
  count: number;
  error?: string;
}

export class CheckinService {
  /**
   * Check into a meeting via API (bypasses RLS timing issues)
   */
  static async checkIn(request: CheckinRequest): Promise<CheckinResponse> {
    try {
      console.log('📝 Check-in via API for meeting:', request.meetingId);

      const result = await apiService.post<{
        success: boolean;
        checkin?: Checkin;
        meeting?: { id: string; name: string; link: string };
        message?: string;
        error?: string;
      }>('/api/checkins', {
        meetingId: request.meetingId,
        meetingDate: request.meetingDate,
        deviceInfo: request.deviceInfo || null,
        location: request.location || null,
        notes: request.notes || null
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to register check-in');
      }

      console.log('✅ Check-in successful via API:', result.checkin?.id);

      // RJ-3b — invalidate today's recovery plan so the Missões row for
      // `participar_encontro` flips to completed on next visit. Dynamic
      // import avoids a service-graph cycle.
      import('./recoveryPlanService')
        .then(({ recoveryPlanService }) => recoveryPlanService.invalidateToday())
        .catch(() => {});

      // PRD §5.6 — `meeting_checkin_submitted` (success branch). The
      // service is the canonical site because both the meeting details
      // screen (`app/meeting/[id]` `handleEntrar`) and the bottom-sheet
      // drawer call this same method — instrumenting here avoids
      // double-counting and missed call sites.
      import('@/utils/clarityEvents')
        .then(({ logMeetingCheckinSubmitted }) =>
          logMeetingCheckinSubmitted({
            meeting_source: 'supabase',
            result: 'success',
            meeting_id: request.meetingId,
          })
        )
        .catch(() => {});

      const response: CheckinResponse = {
        success: true,
        checkin: result.checkin,
        meeting: result.meeting,
        message: result.message || 'Check-in registered successfully'
      };
      
      // Create diary note if callback provided
      if (request.onNoteCreate && response.meeting) {
        await this.createCheckinNote(response.meeting, request.onNoteCreate);
      }
      
      return response;

    } catch (error) {
      console.error('❌ Check-in failed:', error);
      return {
        success: false,
        message: 'Failed to check in',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Check if user is already checked in to a meeting via API (bypasses RLS timing issues)
   */
  static async getCheckinStatus(meetingId: string, date?: string): Promise<CheckinStatus> {
    try {
      console.log('🔍 Getting check-in status via API for meeting:', meetingId);

      let url = `/api/checkins/status/${meetingId}`;
      if (date) {
        url += `?date=${encodeURIComponent(date)}`;
      }

      const result = await apiService.get<{
        success: boolean;
        isCheckedIn: boolean;
        checkin?: Checkin;
        error?: string;
      }>(url);

      if (!result.success) {
        throw new Error(result.error || 'Failed to check status');
      }

      console.log('✅ Check-in status retrieved via API:', result.isCheckedIn);

      return {
        success: true,
        isCheckedIn: result.isCheckedIn,
        checkin: result.checkin
      };

    } catch (error) {
      console.error('❌ Failed to get check-in status:', error);
      return {
        success: false,
        isCheckedIn: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get the 3 most recent unique users who checked into a meeting
   * Uses pagination to ensure we find 3 unique users even in meetings with heavy repeat check-ins
   */
  static async getRecentMeetingAttendees(meetingId: string): Promise<{ userId: string; profilePhotoUrl: string | null }[]> {
    try {
      const uniqueUsers = new Map<string, { userId: string; profilePhotoUrl: string | null; createdAt: string }>();
      const BATCH_SIZE = 100;
      let offset = 0;
      const MAX_FETCHES = 10; // Limit to prevent infinite loops (will check up to 1000 records)
      let fetchCount = 0;

      // Keep fetching batches until we have 3 unique users or run out of data
      while (uniqueUsers.size < 3 && fetchCount < MAX_FETCHES) {
        const { data, error } = await supabase
          .from('meeting_checkins')
          .select('user_id, created_at')
          .eq('meeting_id', meetingId)
          .order('created_at', { ascending: false })
          .range(offset, offset + BATCH_SIZE - 1);

        if (error) {
          console.error('Error fetching meeting attendees:', error);
          break;
        }

        if (!data || data.length === 0) {
          break;
        }

        // Collect unique user IDs from this batch
        const newUserIds = data
          .filter(checkin => !uniqueUsers.has(checkin.user_id))
          .map(checkin => checkin.user_id);

        // Fetch profile photos for new users
        if (newUserIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, profile_photo_url')
            .in('id', newUserIds);

          const profileMap = new Map(profiles?.map(p => [p.id, p.profile_photo_url]) || []);

          // Add new unique users to our map
          for (const checkin of data) {
            if (!uniqueUsers.has(checkin.user_id)) {
              uniqueUsers.set(checkin.user_id, {
                userId: checkin.user_id,
                profilePhotoUrl: profileMap.get(checkin.user_id) || null,
                createdAt: checkin.created_at
              });
              
              if (uniqueUsers.size === 3) break;
            }
          }
        }

        if (data.length < BATCH_SIZE || uniqueUsers.size >= 3) {
          break;
        }

        offset += BATCH_SIZE;
        fetchCount++;
      }

      // Convert to array and return (sorted by most recent check-in)
      return Array.from(uniqueUsers.values())
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 3)
        .map(({ userId, profilePhotoUrl }) => ({ userId, profilePhotoUrl }));
    } catch (error) {
      console.error('Failed to fetch recent meeting attendees:', error);
      return [];
    }
  }

  /**
   * Get user's check-in history via API endpoint (preferred)
   * Uses supabaseAdmin on server to bypass RLS timing issues
   */
  static async getCheckinHistoryViaAPI(limit = 50, offset = 0): Promise<CheckinHistory> {
    try {
      console.log('📋 CheckinService: Loading check-in history via API...');
      
      const response = await apiService.get<{
        success: boolean;
        checkins: CheckinHistory['checkins'];
        count: number;
        error?: string;
      }>(`/api/checkins/history?limit=${limit}&offset=${offset}`);

      if (response.success) {
        console.log('📋 CheckinService: Loaded', response.checkins?.length || 0, 'check-ins via API');
        return {
          success: true,
          checkins: response.checkins || [],
          count: response.count || 0
        };
      } else {
        console.log('ℹ️ Check-in history API returned:', response.error);
        return {
          success: true,
          checkins: [],
          count: 0
        };
      }
    } catch (error) {
      console.log('ℹ️ Could not load check-ins via API:', error);
      return {
        success: true,
        checkins: [],
        count: 0
      };
    }
  }

  /**
   * Get user's check-in history - SECURED WITH SUPABASE RLS
   * @deprecated Use getCheckinHistoryViaAPI instead to avoid Supabase client timing issues
   */
  static async getCheckinHistory(limit = 50, offset = 0): Promise<CheckinHistory> {
    try {
      // Get current user from session
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        return {
          success: false,
          checkins: [],
          count: 0,
          error: 'Authentication required to get history'
        };
      }

      // Query check-ins with meeting details, limited to current user by RLS
      const { data: checkins, error } = await supabase
        .from('meeting_checkins')
        .select(`
          *,
          meetings (
            meeting_name,
            meeting_description,
            meeting_link,
            meeting_time,
            meeting_type
          )
        `)
        .eq('user_id', user.id) // RLS ensures user can only see their own check-ins
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        // Log the error for debugging but don't throw - just return empty
        console.log('ℹ️ Check-ins query returned error (tables may not exist yet):', error.message);
        
        // Return empty result for any database error - this is a non-critical feature
        return {
          success: true,
          checkins: [],
          count: 0
        };
      }

      // Get total count for pagination
      const { count, error: countError } = await supabase
        .from('meeting_checkins')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      if (countError) {
        // Just log the error, don't fail
        console.log('ℹ️ Could not get check-in count:', countError.message);
      }

      return {
        success: true,
        checkins: checkins || [],
        count: count || 0
      };

    } catch (error) {
      console.error('❌ Failed to get check-in history:', error);
      return {
        success: false,
        checkins: [],
        count: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get device information for check-in tracking
   */
  static getDeviceInfo(): string {
    try {
      const platform = require('react-native').Platform;
      return JSON.stringify({
        platform: platform.OS,
        version: platform.Version,
        timestamp: new Date().toISOString()
      });
    } catch {
      return JSON.stringify({
        platform: 'web',
        timestamp: new Date().toISOString()
      });
    }
  }

  /**
   * Format check-in date for display
   */
  static formatCheckinDate(checkinDate: string): string {
    try {
      const date = new Date(checkinDate);
      return date.toLocaleDateString('pt-BR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return checkinDate;
    }
  }

  /**
   * Format check-in time for display
   */
  static formatCheckinTime(checkinDate: string): string {
    try {
      const date = new Date(checkinDate);
      return date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return checkinDate;
    }
  }

  /**
   * Create a diary note for successful check-in
   * @private
   */
  private static async createCheckinNote(
    meeting: { id: string; name: string; link: string },
    onNoteCreate: (content: string, type?: 'note' | 'trail' | 'sentiment' | 'checkin' | 'relapse') => Promise<void>
  ): Promise<void> {
    try {
      const now = new Date();
      const time = now.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      });
      
      const noteContent = `Check-in 📍 Você participou do encontro ${meeting.name} às ${time}`;
      
      await onNoteCreate(noteContent, 'checkin');
      console.log('✅ Check-in note created successfully with type: checkin');
    } catch (error) {
      console.error('❌ Failed to create check-in note:', error);
      // Don't throw error - note creation failure shouldn't affect check-in success
    }
  }
}